import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';
import useAppSelector, {
  useAppDispatch,
} from '../../../app/redux/useAppSelector';
import getCurrentMarkForComment from '../../../post/comment/utils/getCurrentMarkForComment';
import {
  POST_THREADS_QUERY,
  useAddReaction,
  useCreateReaction,
} from '../../../post/MagneticWall';
import { UIActions } from '../../../ui/uiSlice';
import { isReactionOrPublicReactionThread } from '../../../utils/post/threadUtils';
import { EditorActions } from '../../editorSlice';
import { useQuill } from '../../QuillContext';

export default function usePickReaction() {
  const dispatch = useAppDispatch();
  const addReaction = useAddReaction();
  const createReaction = useCreateReaction();
  const magneticWallFocus = useAppSelector(
    (state) => state.editor.magneticWallFocus,
  );
  const postId = useAppSelector((state) => state.editor.postId);
  const currentUser = useAppSelector((state) => state.session.user);
  const sync = useAppSelector((state) => state.editor.sync);

  const { ancestorQuill: quill } = useQuill();

  const client = useApolloClient();

  return useCallback(
    async (picked: string) => {
      if (magneticWallFocus) {
        const { data } = await client.query({
          query: POST_THREADS_QUERY,
          variables: {
            // @ts-expect-error
            postId,
          },
          fetchPolicy: 'cache-first',
        });
        const thread = data.post?.threads?.find(
          (item) => item?.id === magneticWallFocus.id,
        );
        if (!thread || !isReactionOrPublicReactionThread(thread)) {
          return;
        }
        const isReactionActive = thread.reactions?.some(
          (reaction) =>
            reaction?.emoji === picked &&
            reaction?.author?.id === currentUser.id,
        );
        if (!isReactionActive) {
          addReaction(thread, picked);
          dispatch(UIActions.addToRecentlyUsedEmojis(picked));
          dispatch(
            EditorActions.focusMagneticWall({
              id: magneticWallFocus.id,
              type: 'comment',
              isActive: true,
              hasExpandIntent: false,
            }),
          );
        }
      } else if (sync && postId && quill) {
        const { version, checksum } = sync;
        createReaction(
          postId,
          version,
          checksum,
          JSON.stringify(getCurrentMarkForComment(quill)),
          picked,
        );
      }
    },
    [
      postId,
      magneticWallFocus,
      addReaction,
      client,
      dispatch,
      createReaction,
      currentUser.id,
      quill,
      sync,
    ],
  );
}
